import ArrowForward from "@mui/icons-material/ArrowForward";
import { Box, Checkbox, Typography } from "@mui/material";
import type * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import ProfileForm, { type ProfileFormFields } from "components/v3/forms/ProfileForm";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import { FORM_IDS } from "constants/v3/formConstants";
import { PATHS } from "constants/pathConstants";
import type { FormSubmission } from "apis/v3/formTemplatesApi";
import { selectAllFormSubmissions } from "store/reducers/formTemplates/FormTemplatesSlice";
import { selectUserPersonalInformation } from "store/reducers/user/UserAccountSlice";
import { userStateHasGlMemberStatus } from "utils/v3/auth0ClaimsUtils";
import PageTitle from "utils/PageTitle";
import AgreementFormDialog from "./AgreementFormDialog";
import PaidInviteeCompleteSummaryCard from "./PaidInviteeCompleteSummaryCard";
import UnpaidInviteCompleteSummaryCard from "./UnpaidInviteCompleteSummaryCard";
import UnpaidInviteRegistrationStepper from "./UnpaidInviteRegistrationStepper";

const submissionHasFormId = (
  submissions: FormSubmission[],
  formId: string
): boolean =>
  submissions.some((submission) => submission.formInfo.id === formId);

const agreementCheckboxRowSx = {
  alignItems: "flex-start",
  cursor: "pointer",
  display: "flex",
  gap: 1.5
} as const;

const agreementCheckboxControlSx = {
  flexShrink: 0,
  mt: 0.125,
  p: 0.75
} as const;

const UnpaidInviteProfilePage: React.FC = () => {
  PageTitle("Profile information");
  const history = useHistory();
  const allFormSubmissions = useSelector(selectAllFormSubmissions);
  const userPersonalInformation = useSelector(selectUserPersonalInformation);
  // No plan status at all means the member has never paid. A present but
  // inactive status is a paid (possibly lapsed) plan, not an unpaid invitee.
  const requiresPayment = !userStateHasGlMemberStatus(userPersonalInformation);

  const [annualRenewalAcknowledged, setAnnualRenewalAcknowledged] =
    useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const [hipaaDialogOpen, setHipaaDialogOpen] = useState(false);

  const termsAccepted = useMemo(
    () => submissionHasFormId(allFormSubmissions, FORM_IDS.TERMS_AND_CONDITIONS),
    [allFormSubmissions]
  );
  const privacyAccepted = useMemo(
    () => submissionHasFormId(allFormSubmissions, FORM_IDS.PRIVACY_POLICY),
    [allFormSubmissions]
  );
  const hipaaAccepted = useMemo(
    () => submissionHasFormId(allFormSubmissions, FORM_IDS.HIPAA_AUTHORIZATION),
    [allFormSubmissions]
  );

  const agreementsComplete =
    termsAccepted &&
    privacyAccepted &&
    hipaaAccepted &&
    (requiresPayment ? annualRenewalAcknowledged : true);

  const handleFormFieldsUpdated = useCallback((_formFields: ProfileFormFields) => {
    // Reserved for future stepper / summary wiring.
  }, []);

  const handleSubmitted = useCallback(
    (_formFields: ProfileFormFields) => {
      history.push(
        requiresPayment ? PATHS.ONBOARDING_UNPAID_INVITE_PAYMENT : PATHS.LANDING_PAGE
      );
    },
    [history, requiresPayment]
  );

  const renderAboveSubmit = useCallback(() => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%"
        }}
      >
        <Box component="label" sx={agreementCheckboxRowSx}>
          <Checkbox
            checked={termsAccepted}
            onChange={() => {
              setTermsDialogOpen(true);
            }}
            sx={agreementCheckboxControlSx}
          />
          <Typography variant="body_strong_black" sx={{ pt: 0.25 }}>
            I understand the Terms of Use
          </Typography>
        </Box>

        <Box component="label" sx={agreementCheckboxRowSx}>
          <Checkbox
            checked={privacyAccepted}
            onChange={() => {
              setPrivacyDialogOpen(true);
            }}
            sx={agreementCheckboxControlSx}
          />
          <Typography variant="body_strong_black" sx={{ pt: 0.25 }}>
            I understand the Privacy Policy
          </Typography>
        </Box>

        <Box component="label" sx={agreementCheckboxRowSx}>
          <Checkbox
            checked={hipaaAccepted}
            onChange={() => {
              setHipaaDialogOpen(true);
            }}
            sx={agreementCheckboxControlSx}
          />
          <Typography variant="body_strong_black" sx={{ pt: 0.25 }}>
            I understand the HIPAA Authorization
          </Typography>
        </Box>

        {requiresPayment ? (
          <Box component="label" sx={agreementCheckboxRowSx}>
            <Checkbox
              checked={annualRenewalAcknowledged}
              onChange={() =>
                setAnnualRenewalAcknowledged(!annualRenewalAcknowledged)
              }
              sx={agreementCheckboxControlSx}
            />
            <Typography variant="body_strong_black" sx={{ pt: 0.25 }}>
              By checking here you agree that this account will renew automatically
              on an annual basis.
            </Typography>
          </Box>
        ) : null}
      </Box>
    );
  }, [
    annualRenewalAcknowledged,
    hipaaAccepted,
    privacyAccepted,
    requiresPayment,
    termsAccepted
  ]);

  return (
    <GradientFramedLayout showHeader>
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: { md: 3, xs: 2.5 },
          marginX: "auto",
          maxWidth: 1150,
          minWidth: 0,
          pb: { md: 8, xs: 6 },
          pt: { md: 2, xs: 1.5 },
          px: { md: 3, xs: 2 },
          width: "100%"
        }}
      >
        <UnpaidInviteRegistrationStepper
          activeStepIndex={1}
          showPaymentStep={requiresPayment}
        />

        <Box
          sx={{
            alignItems: "flex-start",
            display: "flex",
            flexDirection: { lg: "row", xs: "column" },
            gap: { lg: 3, xs: 2.5 },
            width: "100%"
          }}
        >
          <Box
            sx={{
              flex: { lg: "1 1 0%", xs: "none" },
              minWidth: 0,
              order: { lg: 0, xs: 1 },
              width: "100%"
            }}
          >
            <ProfileForm
              extraSubmitEnabled={agreementsComplete}
              headingSubtitle="Complete your profile to continue enrollment. Legal name and date of birth must match your account; contact and mailing information help us reach you and ship materials."
              headingTitle="Profile information"
              onFormFieldsUpdated={handleFormFieldsUpdated}
              onSubmitted={handleSubmitted}
              renderAboveSubmit={renderAboveSubmit}
              submitButtonLeftIcon={null}
              submitButtonRightIcon={<ArrowForward />}
              submitButtonText="Continue"
            />
          </Box>

          <Box
            sx={{
              flex: { lg: "0 0 auto", xs: "none" },
              order: { lg: 1, xs: 0 },
              position: { lg: "sticky", xs: "static" },
              top: { lg: 16, xs: "auto" },
              width: { lg: "auto", xs: "100%" }
            }}
          >
            {requiresPayment ? (
              <UnpaidInviteCompleteSummaryCard />
            ) : (
              <PaidInviteeCompleteSummaryCard />
            )}
          </Box>
        </Box>
      </Box>

      <AgreementFormDialog
        formId={FORM_IDS.TERMS_AND_CONDITIONS}
        initialConfirmationChecked={termsAccepted}
        onAgreed={() => undefined}
        onClose={() => setTermsDialogOpen(false)}
        open={termsDialogOpen}
        title="Terms & Conditions"
      />
      <AgreementFormDialog
        formId={FORM_IDS.PRIVACY_POLICY}
        initialConfirmationChecked={privacyAccepted}
        onAgreed={() => undefined}
        onClose={() => setPrivacyDialogOpen(false)}
        open={privacyDialogOpen}
        title="Privacy Policy"
      />
      <AgreementFormDialog
        formId={FORM_IDS.HIPAA_AUTHORIZATION}
        initialConfirmationChecked={hipaaAccepted}
        onAgreed={() => undefined}
        onClose={() => setHipaaDialogOpen(false)}
        open={hipaaDialogOpen}
        title="HIPAA Authorization"
      />
    </GradientFramedLayout>
  );
};

export default UnpaidInviteProfilePage;
